import { createContext, useCallback, useContext, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "gx_admin_jwt";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const setSession = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    setToken(newToken || "");
  }, []);

  const authFetch = useCallback(
    async (path, opts = {}) => {
      const t = opts.token || localStorage.getItem(TOKEN_KEY) || token;
      const headers = {
        "Content-Type": "application/json",
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
        ...(opts.headers || {}),
      };
      const res = await fetch(`${API}${path}`, { ...opts, headers });
      const raw = await res.text();
      let data;
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = { raw };
      }
      if (!res.ok) {
        const err = new Error(data?.detail || `HTTP ${res.status}`);
        err.status = res.status;
        if (res.status === 401) setSession("");
        throw err;
      }
      return data;
    },
    [token, setSession]
  );

  const login = useCallback(
    async (email, password) => {
      const data = await authFetch("/api/admin/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
        token: "",
      });
      setSession(data.token);
      setAdmin({ email: data.email });
      return data;
    },
    [authFetch, setSession]
  );

  const logout = useCallback(() => {
    setSession("");
    setAdmin(null);
  }, [setSession]);

  const downloadCsv = useCallback(
    async (path, fallbackName = "export.csv") => {
      const t = localStorage.getItem(TOKEN_KEY) || token;
      if (!t) throw new Error("Not authenticated");
      const res = await fetch(`${API}${path}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) {
        if (res.status === 401) setSession("");
        const detail = await res.text().catch(() => "");
        throw new Error(detail || `HTTP ${res.status}`);
      }
      // Prefer the filename the server suggested.
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = (m && m[1]) || fallbackName;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      return filename;
    },
    [token, setSession]
  );

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!token) {
        setAdmin(null);
        setLoading(false);
        return;
      }
      try {
        const me = await authFetch("/api/admin/auth/me");
        if (!cancelled) setAdmin(me);
      } catch {
        if (!cancelled) setAdmin(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    verify();
    return () => {
      cancelled = true;
    };
  }, [token, authFetch]);

  const value = { admin, token, loading, login, logout, authFetch, downloadCsv, isAuthed: !!admin };
  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}
